import { GoabButton, GoabButtonGroup, GoabCallout, GoabCheckbox, GoabModal, GoabRadioGroup, GoabRadioItem } from '@abgov/react-components';
import { GoabCheckboxOnChangeDetail, GoabRadioGroupOnChangeDetail } from '@abgov/ui-components-common';
import './MealInfo.scss';
import { useEffect, useState } from 'react';
import { MealsClaimsExpenses } from '../../../../../common/types';


interface MealProps {
  updateFlow: boolean;
  canShowEdit?: boolean;
  mealsCanClaim: MealsClaimsExpenses;
  activeSection?: string | null;
  isBusy?: boolean;
  onChange: (changeType: string, change: MealsClaimsExpenses | boolean | null) => void;
}

export function MealInfo(props: MealProps) {

  type DeleteModalType = 'deselect-some' | 'deselect-all';

  const [secStatus, setSecStatus]   = useState({editMode: !props.updateFlow});
  const [localMeals, setLocalMeals] = useState<MealsClaimsExpenses>({breakfast: false,lunch: false,dinner: false,lodging: false});
  const [showDeleteModal, setShowDeleteModal] = useState<{ type: DeleteModalType } | null>(null);
  
  const hasMealsInProps = (): boolean =>(props.mealsCanClaim?.breakfast ?? false) ||(props.mealsCanClaim?.lunch ?? false) ||(props.mealsCanClaim?.dinner ?? false) ||(props.mealsCanClaim?.lodging ?? false);
  const hasMealsInLocal = (): boolean =>localMeals.breakfast || localMeals.lunch || localMeals.dinner || localMeals.lodging;
  const [eligibility, setEligibility] = useState<{original: boolean; local: boolean}>({original: hasMealsInProps(),local:    hasMealsInLocal()});
  const showMealOptions = eligibility.local === true;
  const isUpdateFlow = props.updateFlow;
  
  
  /*********************************************************************************
   * 
   * Check if section is dirty (different from original props)
  *********************************************************************************/
  function isDirty(): boolean {
    // Check if meal selections changed
    return (
      localMeals.breakfast !== (props.mealsCanClaim?.breakfast ?? false) ||
      localMeals.lunch !== (props.mealsCanClaim?.lunch ?? false) ||
      localMeals.dinner !== (props.mealsCanClaim?.dinner ?? false) ||
      localMeals.lodging !== (props.mealsCanClaim?.lodging ?? false)
    );
  }
  const isSectionDirty = isDirty();
  const isSaveEnabled = isSectionDirty;
  /***************************************************************************************
  * . SYNC INTERNAL STATE WHEN PROPS CHANGE
  ****************************************************************************************/
  useEffect(() => {
    if (!props.mealsCanClaim) return;

    setLocalMeals({
      breakfast: props.mealsCanClaim.breakfast,
      lunch:     props.mealsCanClaim.lunch,
      dinner:    props.mealsCanClaim.dinner,
      lodging:   props.mealsCanClaim.lodging
    });
  }, [props.mealsCanClaim]);

  useEffect(() => {
    const val = hasMealsInProps();
    setEligibility({ original: val, local: val });
  }, [props.mealsCanClaim, props.updateFlow]);

  useEffect(() => {
    setSecStatus({ editMode: !props.updateFlow });
  }, [props.updateFlow]);

  // Enforce single-active-section restriction: if another section becomes the active
  // one being edited, auto-cancel this section's own in-progress edit.
  useEffect(() => {
    if (props.activeSection !== undefined && props.activeSection !== 'meal' && secStatus.editMode && props.updateFlow) {
      onCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeSection]);

  /********************************************************************************
   * 
  *********************************************************************************/
  function handleEligibility(detail: GoabRadioGroupOnChangeDetail) {
    const newEligibility = detail.value === "1";
    setEligibility(prev => ({ ...prev, local: newEligibility }));

    if (!newEligibility) {
      setLocalMeals({breakfast: false,lunch: false,dinner: false,lodging: false});
    }

    props.onChange("eligibility", newEligibility);

  }
  /********************************************************************************
   * 
  *********************************************************************************/
  function handleCheckboxChange (detail: GoabCheckboxOnChangeDetail){
    const field  =  detail.name as keyof MealsClaimsExpenses;
    const updatedMeals = { ...localMeals, [field]: detail.checked };
    setLocalMeals(updatedMeals);

    // Check if all checkboxes are now unchecked
    const hasAnyMealSelected = updatedMeals.breakfast || updatedMeals.lunch || updatedMeals.dinner || updatedMeals.lodging;
    
    // If eligibility is "Yes" but no meals are selected, auto-revert to "No"
    if (eligibility.local === true && !hasAnyMealSelected) {
      setEligibility(prev => ({ ...prev, local: false }));
    }
  }
  
  /********************************************************************************
   * 
  *********************************************************************************/
  function onEdit(){
    props.onChange("editStart", true);
    setSecStatus({editMode:!secStatus.editMode});
  }
  /********************************************************************************
   *
  *********************************************************************************/
  function onCancel(){
    const val = hasMealsInProps();
    setEligibility({ original: val, local: val });
    setLocalMeals({
      breakfast: props.mealsCanClaim?.breakfast ?? false,
      lunch: props.mealsCanClaim?.lunch ?? false,
      dinner: props.mealsCanClaim?.dinner ?? false,
      lodging: props.mealsCanClaim?.lodging ?? false
    });

    setSecStatus({editMode: !props.updateFlow});
  }
  /********************************************************************************
   * OnSave - confirmation gate only
  *********************************************************************************/
  function onSave(){
    const hasAnyMealSelected = localMeals.breakfast || localMeals.lunch || localMeals.dinner || localMeals.lodging;

    const hasDeletionImpact =
      (props.mealsCanClaim?.breakfast ?? false) && !localMeals.breakfast ||
      (props.mealsCanClaim?.lunch ?? false) && !localMeals.lunch ||
      (props.mealsCanClaim?.dinner ?? false) && !localMeals.dinner ||
      (props.mealsCanClaim?.lodging ?? false) && !localMeals.lodging;

    if (hasDeletionImpact) {
      const modalType: DeleteModalType = hasAnyMealSelected ? 'deselect-some' : 'deselect-all';
      setShowDeleteModal({ type: modalType });
      return;
    }

    doSave();
  }

  /********************************************************************************
   * doSave - validation and execute save
  *********************************************************************************/
  function doSave(){
    
    // ready to inform the parent that the rows are valid and can be saved
    props.onChange("save", localMeals);
    
    // In create flow there is no Edit button, so keep section editable after save.
    setSecStatus({editMode: !props.updateFlow});
  }

  function onCancelDeleteModal() {
    setShowDeleteModal(null);

    const val = hasMealsInProps();
    setEligibility({ original: val, local: val });
    setLocalMeals({
      breakfast: props.mealsCanClaim?.breakfast ?? false,
      lunch: props.mealsCanClaim?.lunch ?? false,
      dinner: props.mealsCanClaim?.dinner ?? false,
      lodging: props.mealsCanClaim?.lodging ?? false
    });
  }

  function onConfirmDeleteModal() {
    setShowDeleteModal(null);
    doSave();
  }
  /********************************************************************************
   * 
  *********************************************************************************/
  return (
    <section className="meal-section">
      {showMealOptions && secStatus.editMode && (
        <GoabCallout type="information" emphasis="low" maxWidth="100%" testId="tic-meal-sec-helper-callout">
          Email pre-approval is required for meals and lodging eligibility.
        </GoabCallout>
      )}
      <div className="header-and-actions">
        <p>Are you eligible to claim expenses for the work performed?</p>
        <div className="header-actions">
          {isUpdateFlow ? (
            secStatus.editMode ? (
              <>
                <GoabButton type='text' size='compact' onClick={() => onCancel()}>Cancel</GoabButton>
                <GoabButton type='secondary' size='compact' onClick={() => onSave()} disabled={!isSaveEnabled || (props.isBusy ?? false)}>Save</GoabButton>
              </>
            ) : (
              (props.canShowEdit ?? false) && <GoabButton type='text' size='compact' disabled={props.isBusy ?? false} onClick={() => onEdit()}>Edit</GoabButton>
            )
          ) : null}
        </div>
      </div>
      {secStatus.editMode && isUpdateFlow ?
      <GoabRadioGroup name="Are_you_eligible" value={eligibility.local ? "1" : "2"} onChange={handleEligibility} disabled={!secStatus.editMode || !isUpdateFlow}>
        <GoabRadioItem value="1" label="Yes" />
        <GoabRadioItem value="2" label="No" />
      </GoabRadioGroup>
      :<span>{eligibility.local ? "Yes" : "No"}</span>}
      {showMealOptions && <p>{secStatus.editMode ? "Select the options that apply" : "Selected options"}</p>}
      {secStatus.editMode ?  (
        showMealOptions && <div className="rowG g-checkboxes">
          <GoabCheckbox testId="meal_breakfast_chkbx" text="Breakfast"  name="breakfast"  value={1} checked={localMeals.breakfast} disabled={!secStatus.editMode} onChange={(details: GoabCheckboxOnChangeDetail) => { handleCheckboxChange(details ) }} />
          <GoabCheckbox testId="meal_lunch_chkbx"     text="Lunch"      name="lunch"      value={1} checked={localMeals.lunch}     disabled={!secStatus.editMode} onChange={(details: GoabCheckboxOnChangeDetail) => { handleCheckboxChange(details ) }} />
          <GoabCheckbox testId="meal_dinner_chkbx"    text="Dinner"     name="dinner"     value={1} checked={localMeals.dinner}    disabled={!secStatus.editMode} onChange={(details: GoabCheckboxOnChangeDetail) => { handleCheckboxChange(details ) }} />
          <GoabCheckbox testId="meal_lodging_chkbx"   text="Lodging"    name="lodging"    value={1} checked={localMeals.lodging}   disabled={!secStatus.editMode} onChange={(details: GoabCheckboxOnChangeDetail) => { handleCheckboxChange(details ) }} />
          <div></div>
        </div>
      ):(showMealOptions && <div className="rowG readonly">
        {localMeals.breakfast && <span>Breakfast</span>}
        {localMeals.lunch && <span>Lunch</span>}
        {localMeals.dinner && <span>Dinner</span>}
        {localMeals.lodging&& <span>Lodging</span>}
      </div>) 
    }
      <GoabModal heading={'Confirmation: Delete meal and lodging entries'} calloutVariant='emergency' open={showDeleteModal !== null} maxWidth='30vw'
        actions={
          <GoabButtonGroup alignment='end'>
            <GoabButton testId='ud-delete-meal-entries-modal-no' type='secondary' size='compact' onClick={onCancelDeleteModal}>No, cancel</GoabButton>
            <GoabButton testId='ud-delete-meal-entries-modal-yes' type='primary' size='compact' onClick={onConfirmDeleteModal}>Yes, delete</GoabButton>
          </GoabButtonGroup>
        }
      ><p>{showDeleteModal?.type === 'deselect-all'
          ? <>The row(s) associated with the unselected option(s) will be deleted from the Cost Summary table and expense eligibility will be reset to &quot;No.&quot;</>
          : <>The row(s) associated with the unselected option(s) will be deleted from the Cost Summary table.</>}
        </p>
      </GoabModal>
      
    </section>
  );
}